<template>
  <div>
    <div class="grid gap-4 py-6 border-t bg-gray-50 md:grid-cols-3 auto-rows-auto lg:grid-cols-5">
      <DatePicker class="col-span-2" color="indigo" mode="dateTime" :minute-increment="5" is-range is24hr v-model="filter.date" :model-config="{ start: { timeAdjust: '00:00:00' }, end: { timeAdjust: '23:59:59' } }">
        <template v-slot="{ inputValue, inputEvents }">
          <div class="flex justify-center">
            <input
              :value="inputValue.start"
              v-on="inputEvents.start"
              class="w-40 form-input prepend"
            />
            <div class="flex items-center justify-center flex-none w-10 border bg-gray-50">
              &rarr;
            </div>
            <input
              :value="inputValue.end"
              v-on="inputEvents.end"
              class="w-40 form-input append"
            />
          </div>
        </template>
      </DatePicker>
      <DepotSelect v-model="filter.depot" />
    </div>

    <div>
      <div v-if="report">
        <!-- No Results -->
        <div v-if="report.rows.length === 0" class="p-8 m-6 text-center text-gray-500 rounded-b-lg bg-gray-50">
          No results. Try broadening your query parameters.
        </div>
        <!-- Results -->
        <div class="card" v-else>
          <div class="flex flex-col justify-between p-4 border-b md:flex-row lg:p-5">
            <button class="ml-auto btn btn-sm btn-outline" @click="downloadReport" v-submitting="submitting">
              <fa :icon="['far', 'arrow-to-bottom']" class="mr-2" /> Download
            </button>
          </div>
          <div class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-400 uppercase bg-gray-50">
            Summary
          </div>
          <div class="grid border-t border-b lg:divide-x md:grid-cols-5">
            <div class="p-8">
              <div class="flex items-center text-3xl font-bold">
                {{ report.data.complete }}/{{ report.data.total }}
                <div class="inline-flex px-2 ml-3 text-xl font-normal text-gray-500 bg-gray-100 rounded">
                  {{ report.data.complete_percent }}%
                </div>
              </div>
              <div class="text-xs font-medium tracking-wider text-gray-500 uppercase">Complete</div>
            </div>
            <div class="p-8">
              <div class="flex items-center text-3xl font-bold">
                {{ report.data.early_starts }} 
                <div class="inline-flex px-2 ml-3 text-xl font-normal text-gray-500 bg-gray-100 rounded">
                  {{ report.data.early_starts_percent }}%
                </div>
              </div>
              <div class="text-xs font-medium tracking-wider text-gray-500 uppercase">Early Starts</div>
            </div>
            <div class="p-8">
              <div class="flex items-center text-3xl font-bold">
                {{ report.data.late_starts }} 
                <div class="inline-flex px-2 ml-3 text-xl font-normal text-gray-500 bg-gray-100 rounded">
                  {{ report.data.late_starts_percent }}%
                </div>
              </div>
              <div class="text-xs font-medium tracking-wider text-gray-500 uppercase">Late Starts</div>
            </div>
            <div class="p-8">
              <div class="flex items-center text-3xl font-bold">
                {{ report.data.early_finishes }} 
                <div class="inline-flex px-2 ml-3 text-xl font-normal text-gray-500 bg-gray-100 rounded">
                  {{ report.data.early_finishes_percent }}%
                </div>
              </div>
              <div class="text-xs font-medium tracking-wider text-gray-500 uppercase">Early Finishes</div>
            </div>
            <div class="p-8">
              <div class="flex items-center text-3xl font-bold">
                {{ report.data.late_finishes }} 
                <div class="inline-flex px-2 ml-3 text-xl font-normal text-gray-500 bg-gray-100 rounded">{{ report.data.late_finishes_percent }}%</div>
              </div>
              <div class="text-xs font-medium tracking-wider text-gray-500 uppercase">Late Finishes</div>
            </div>
          </div>
          <data-table
            small
            :items="report.rows" 
            :keys="[
              { name: 'Name', slug: 'name' },
              { name: 'Vehicle', slug: 'vehicle' },
              { name: 'Vehicle No.', slug: 'vehicle_number' },
              { name: 'Driver', slug: 'driver' },
              { name: 'Driver No.', slug: 'driver_number' },
              { name: 'Planned Departure Time', slug: 'planned_departure_time' },
              { name: 'Planned Arrival Time', slug: 'planned_arrival_time' },
              { name: 'Actual Departure Time', slug: 'actual_departure_time' },
              { name: 'Actual Arrival Time', slug: 'actual_arrival_time' },
              { name: 'Projected Arrival Time', slug: 'projected_arrival_time' }
            ]" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { saveAs } from 'file-saver'
import { dragscroll } from 'vue-dragscroll'
import { sub } from 'date-fns'
import moment from 'moment'
import * as ExcelJS from 'exceljs'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
import DepotSelect from '@/components/DepotSelect'

export default {
  name: 'DepotArrivalReport',

  directives: {
    dragscroll
  },

  components: {
    DatePicker,
    DepotSelect
  },

  props: {
    report: {
      type: Object
    }
  },

  computed: {
    params () {
      return { 
        filter: this.filter,
      }
    }
  },

  data () {
    return {
      submitting: false,
      filter: {
        date: {
          start: sub(new Date(), { days: 1 }),
          end: new Date()
        },
        depot: null
      }
    }
  },

  methods: {
    async downloadReport () {
      this.submitting = true
      const filename = `Depot Arrival Reoprt.xlsx`
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Report')

      worksheet.addRow(['Report Date', `${new Date()}`])
      worksheet.addRow(['Depot', `${this.report.data.depot.name}`])
      worksheet.addRow(['Date Range', `${moment(this.report.data.filter.date.start).format('DD-MM-YYYY HH:mm')} - ${moment(this.report.data.filter.date.end).format('DD-MM-YYYY HH:mm')}`])
      worksheet.addRow([])
      worksheet.addRow(['Total Routes', `${this.report.data.total}`])
      worksheet.addRow(['Complete Routes', `${this.report.data.complete}`, `${this.report.data.complete_percent}%`])
      worksheet.addRow(['Early Starts', `${this.report.data.early_starts}`, `${this.report.data.early_starts_percent}%`])
      worksheet.addRow(['Early Finishes', `${this.report.data.early_finishes}`, `${this.report.data.early_finishes_percent}%`])
      worksheet.addRow(['Late Starts', `${this.report.data.late_starts}`, `${this.report.data.late_starts_percent}%`])
      worksheet.addRow(['Late Finishes', `${this.report.data.late_finishes}`, `${this.report.data.late_finishes_percent}%`])
      worksheet.addRow([])
      worksheet.addRow([`Name`, `Vehicle`, `Vehicle No.`, `Driver`, `Driver No.`, `Planned Departure Time`, `Planned Arrival Time`, `Actual Departure Time`, `Actual Arrival Time`])


      for (let row of this.report.rows) {
        worksheet.addRow([
          `${row.name}`, 
          `${row.vehicle}`,
          `${row.vehicle_number}`,
          `${row.driver}`,
          `${row.driver_number}`,
          `${row.planned_departure_time}`,
          `${row.planned_arrival_time}`,
          `${row.actual_departure_time}`,
          `${row.actual_arrival_time}`
        ])
      }

      worksheet.getRow(12).font = { bold: true }

      worksheet.getColumn(1).font = { bold: true }
      worksheet.getColumn(1).width = 25
      worksheet.getColumn(2).width = 25
      worksheet.getColumn(3).width = 25
      worksheet.getColumn(4).width = 25
      worksheet.getColumn(5).width = 25
      worksheet.getColumn(6).width = 25
      worksheet.getColumn(7).width = 25
      worksheet.getColumn(8).width = 25
      worksheet.getColumn(9).width = 25

      workbook.xlsx.writeBuffer()
        .then((buffer) => {
          saveAs(new Blob([buffer]), filename)
        })
        .catch((error) => {
          console.log('Error writing excel export', error)
        })

      this.submitting = false
    },
  }
}
</script>